import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { LoadingBlock } from "@/components/LoadingState";
import { ConfirmButton } from "@/components/ConfirmButton";
import {
    ArrowLeft,
    CheckCircle2,
    Download,
    FileText,
    FolderDown,
    RotateCcw,
    Scale,
    Share2,
    Sheet,
} from "lucide-react";
import { toast } from "sonner";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import {
    useUserMatch,
    useDeleteUserMatchCascade,
    useRestoreUserMatch,
} from "@/queries/matches";
import { formatSvDateTime } from "@/utils/date";
import HeatProgramMatrix from "@/components/protocol/HeatProgramMatrix";
import TeamLogo from "@/components/TeamLogo";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSavedDetailSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";
import { saveProtocolCsv, shareProtocolCsv } from "@/lib/protocolCsvExport";
import { saveProtocolPdf, shareProtocolPdf } from "@/lib/protocolPdfExport";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from "@/components/ui/dialog";

const STATUS_LABEL = {
    validated: "Validerad",
    disputed: "Avvikelse",
    completed: "Komplett",
    complete: "Komplett",
};

const STATUS_VARIANT = {
    validated: "default",
    disputed: "destructive",
    completed: "secondary",
    complete: "secondary",
};

function formatDateTime(value) {
    return (
        formatSvDateTime(value, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }) || "Datum saknas"
    );
}

function computeRiderTotals(heats = []) {
    const totals = new Map();
    heats.forEach((heat) => {
        const riders = heat?.riders || {};
        (heat?.results || []).forEach((res) => {
            const entry = Object.values(riders).find(
                (r) => String(r?.rider_id ?? "") === String(res?.rider_id ?? "")
            );
            const riderId = String(res?.rider_id ?? entry?.rider_id ?? "");
            if (!riderId) return;
            const existing = totals.get(riderId) || {
                riderId,
                name: entry?.name ?? riderId,
                team: entry?.team ?? "unknown",
                heats: 0,
                points: 0,
                bonus: 0,
            };
            existing.heats += 1;
            existing.points += Number(res?.points ?? 0);
            existing.bonus += Number(res?.bonus_points ?? 0);
            totals.set(riderId, existing);
        });
    });

    const grouped = { home: [], away: [], unknown: [] };
    totals.forEach((value) => {
        const total = value.points;
        const item = { ...value, total };
        const bucket = grouped[value.team] ?? grouped.unknown;
        bucket.push(item);
    });

    Object.values(grouped).forEach((arr) => {
        arr.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
    });

    return grouped;
}

function buildRiderContextMap(matchDetails = {}) {
    const map = new Map();
    const collect = (entries = [], teamKey = "") => {
        const teamId = teamKey === "home" ? matchDetails?.home_team_id : matchDetails?.away_team_id;
        entries.forEach((entry) => {
            const riderId = String(entry?.rider_id || entry?.id || "");
            if (!riderId) return;
            const existing = map.get(riderId) || {};
            map.set(riderId, {
                ...existing,
                ...entry,
                rider_id: riderId,
                id: riderId,
                context_team_id: entry?.context_team_id || existing.context_team_id || teamId || "",
            });
        });
    };

    ["lineups", "initial_lineups", "source_lineups"].forEach((key) => {
        const lineups = matchDetails?.[key] || {};
        collect(lineups?.home || [], "home");
        collect(lineups?.away || [], "away");
    });

    return map;
}

function TeamHeaderColumn({ teamName, fallbackLetter }) {
    return (
        <div className="native-v3-saved-team flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
            <div className="native-v3-saved-team-logo relative h-14 w-14 shrink-0 border border-border/70 bg-muted/30 sm:h-16 sm:w-16">
                <TeamLogo
                    teamName={teamName}
                    size={42}
                    className="absolute inset-0 m-auto h-[42px] w-[42px] object-contain sm:h-[46px] sm:w-[46px]"
                    fallback={
                        <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold uppercase text-muted-foreground">
                            {fallbackLetter}
                        </div>
                    }
                />
            </div>
            <span className="max-w-full text-sm font-semibold text-foreground sm:text-base">{teamName}</span>
        </div>
    );
}

export default function SavedMatchDetailPage() {
    const { userMatchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { activeProtocolMatchId, clearProtocolSession, requestProtocolSession } = useProtocolShell();

    const { data, isLoading, error } = useUserMatch(userMatchId);
    const deleteMutation = useDeleteUserMatchCascade();
    const restoreMutation = useRestoreUserMatch();
    const [isExporting, setIsExporting] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportMode, setExportMode] = useState(null);
    const requestedReturnTo = location.state?.returnTo;
    const savedListPath = (
        typeof requestedReturnTo === "string"
        && /^\/account\/matches\/saved-protocols(?:\?|$)/.test(requestedReturnTo)
    )
        ? requestedReturnTo
        : "/account/matches/saved-protocols";

    const riderTotals = useMemo(
        () => computeRiderTotals(data?.user_results?.heats || []),
        [data?.user_results?.heats]
    );
    const riderContextMap = useMemo(
        () => buildRiderContextMap(data?.match_details || {}),
        [data?.match_details]
    );

    const title = `${data?.match_details?.home_team ?? "Hemmalag"} vs ${data?.match_details?.away_team ?? "Bortalag"}`;

    const statusKey = (data?.status || "complete").toLowerCase();
    const statusLabel = STATUS_LABEL[statusKey] ?? "Komplett";
    const statusVariant = STATUS_VARIANT[statusKey] ?? "secondary";
    const officialScore = data?.official_results
        ? `${data?.official_results?.home_score ?? "-"} - ${data?.official_results?.away_score ?? "-"}`
        : null;

    const officialMatchesUser =
        data?.official_results &&
        data?.user_results &&
        Number(data?.official_results?.home_score) === Number(data?.user_results?.home_score) &&
        Number(data?.official_results?.away_score) === Number(data?.user_results?.away_score);

    const handleDelete = async () => {
        if (!data?.id) return;
        await deleteMutation.mutateAsync(data.id);
        if (
            data?.match_id &&
            activeProtocolMatchId &&
            String(activeProtocolMatchId) === String(data.match_id)
        ) {
            clearProtocolSession();
        }
        navigate(savedListPath, { replace: true });
    };

    const handleRestore = async () => {
        if (!data?.id) return;
        const restored = await toast.promise(restoreMutation.mutateAsync(data.id), {
            loading: "Återställer protokollet...",
            success: "Protokollet återställdes och kan redigeras igen.",
            error: (e) => e?.message || e?.detail || "Kunde inte återställa protokollet",
        });

        if (restored?.match_id) {
            requestProtocolSession(restored.match_id);
        }

        navigate("/account/matches/created-protocols", { replace: true });
    };

    const handleExport = async (format, mode) => {
        if (!data || isExporting) return;
        setIsExporting(true);
        setExportDialogOpen(false);
        setExportMode(null);
        try {
            if (mode === "save") {
                const result = format === "pdf" ? await saveProtocolPdf(data) : await saveProtocolCsv(data);
                toast.success(`${result.filename} sparades i ${result.location}.`);
            } else {
                if (format === "pdf") await shareProtocolPdf(data);
                else await shareProtocolCsv(data);
            }
        } catch (error) {
            const cancelled = /cancel|dismiss|abort/i.test(String(error?.message || error || ""));
            if (!cancelled) {
                toast.error(error?.message || "Kunde inte exportera protokollet.");
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="native-v3-saved-detail">
            <div className="hidden sm:block">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/account/matches/created-protocols">Mina matcher</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to={savedListPath}>Sparade protokoll</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>{title}</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            <div className="native-v3-saved-detail-back flex flex-wrap items-center justify-start gap-2 sm:mt-0">
                <Button className="native-v3-saved-detail-back-button" variant="ghost" size="sm" asChild>
                    <Link to={savedListPath} className="flex items-center gap-1">
                        <ArrowLeft className="h-4 w-4" /> Tillbaka
                    </Link>
                </Button>
            </div>

            <div className="mt-2">
            {isLoading ? (
                getNativePlatform() === "android" ? <NativeSavedDetailSkeleton /> : <LoadingBlock text="Hämtar match" />
            ) : error ? (
                <Card>
                    <CardContent className="py-10 text-center text-sm text-red-600">
                        Kunde inte hämta den sparade matchen.
                    </CardContent>
                </Card>
            ) : !data ? (
                <Card>
                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                        Matchen saknas eller har tagits bort.
                    </CardContent>
                </Card>
            ) : (
                <div className="native-v3-saved-detail-grid grid min-w-0 gap-6 lg:grid-cols-[3fr,2fr]">
                    <Card className="native-v3-saved-detail-main min-w-0 overflow-hidden lg:col-span-2">
                        <CardHeader className="native-v3-saved-detail-hero space-y-5">
                            <div className="native-v3-saved-detail-meta flex flex-wrap items-center justify-between gap-2">
                                <Badge className="native-v3-status-badge" variant={statusVariant}>
                                    {statusLabel}
                                </Badge>
                                <CardDescription className="text-right text-xs">
                                    Sparat {formatDateTime(data?.completed_at)}
                                </CardDescription>
                            </div>
                            <div className="native-v3-saved-detail-teams flex items-start gap-3 sm:gap-5">
                                <TeamHeaderColumn
                                    teamName={data?.match_details?.home_team ?? "Hemmalag"}
                                    fallbackLetter="H"
                                />
                                <div className="flex shrink-0 flex-col items-center justify-center gap-2 px-1 pt-2 text-center">
                                    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                                        vs
                                    </div>
                                </div>
                                <TeamHeaderColumn
                                    teamName={data?.match_details?.away_team ?? "Bortalag"}
                                    fallbackLetter="B"
                                />
                            </div>
                        </CardHeader>
                        <CardContent className="native-v3-saved-detail-content min-w-0 space-y-6">
                            <div className="native-v3-saved-detail-scores grid gap-4 sm:grid-cols-2">
                                <div className="native-v3-saved-score border bg-muted/30 p-4">
                                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                                        <CheckCircle2 className="h-4 w-4" /> Ditt resultat
                                    </h3>
                                    <p className="text-2xl font-bold">
                                        {data?.user_results?.home_score ?? 0}
                                        <span className="text-muted-foreground"> - </span>
                                        {data?.user_results?.away_score ?? 0}
                                    </p>
                                </div>
                                <div className="native-v3-saved-score border bg-muted/30 p-4">
                                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                                        <Scale className="h-4 w-4" /> Officiellt resultat
                                    </h3>
                                    <p className="text-2xl font-bold">
                                        {officialScore ?? "–"}
                                    </p>
                                    {officialScore && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {officialMatchesUser ? "Matchar ditt protokoll" : "Matchar inte ditt protokoll"}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="native-v3-saved-detail-actions flex flex-wrap gap-2">
                                <Button
                                    className="native-v3-saved-export-action"
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setExportMode(null);
                                        setExportDialogOpen(true);
                                    }}
                                    disabled={isExporting}
                                >
                                    <Download className="h-4 w-4" />
                                    {isExporting ? "Förbereder..." : "Exportera protokoll"}
                                </Button>
                                <ConfirmButton
                                    title="Återställ sparat protokoll?"
                                    description="Detta flyttar tillbaka protokollet till skapade protokoll så att du kan fortsätta redigera det."
                                    confirmText="Återställ"
                                    cancelText="Avbryt"
                                    triggerVariant="outline"
                                    triggerSize="sm"
                                    onConfirm={handleRestore}
                                    disabled={restoreMutation.isPending}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <RotateCcw className="h-4 w-4" />
                                        Återställ protokoll
                                    </span>
                                </ConfirmButton>
                                <ConfirmButton
                                    title="Ta bort sparad match?"
                                    description="Tar bort protokollet permanent."
                                    confirmText="Ta bort"
                                    cancelText="Avbryt"
                                    actionVariant="destructive"
                                    triggerVariant="outline"
                                    triggerSize="sm"
                                    onConfirm={handleDelete}
                                    disabled={deleteMutation.isPending}
                                >
                                    Ta bort protokoll
                                </ConfirmButton>
                            </div>

                            <Separator className="native-v3-saved-detail-separator" />

                            <div className="native-v3-saved-protocol min-w-0 space-y-3">
                                <div>
                                    <h2 className="text-lg font-semibold">Ditt Protokoll</h2>
                                </div>
                                <div className="w-full max-w-full min-w-0 overflow-hidden">
                                    <HeatProgramMatrix
                                        heats={data?.user_results?.heats}
                                        lineups={data?.match_details?.lineups}
                                        sourceLineups={data?.match_details?.source_lineups}
                                        lineupSubstitutions={data?.match_details?.lineup_substitutions}
                                        homeTeamName={data?.match_details?.home_team ?? "Hemmalag"}
                                        awayTeamName={data?.match_details?.away_team ?? "Bortalag"}
                                        showResults
                                        showLineupChangeStatus
                                        emptyText="Inga heat finns sparade för detta protokoll."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Dialog
                        open={exportDialogOpen}
                        onOpenChange={(open) => {
                            setExportDialogOpen(open);
                            if (!open) setExportMode(null);
                        }}
                    >
                        <DialogContent className="native-v3-export-dialog">
                            <div className="native-v3-export-dialog-heading">
                                <Download aria-hidden="true" />
                                <span>Exportera protokoll</span>
                            </div>
                            <DialogTitle>{exportMode ? "Välj filformat" : "Vad vill du göra?"}</DialogTitle>
                            <DialogDescription className={exportMode ? "sr-only" : undefined}>
                                {exportMode
                                    ? "Välj filformat."
                                    : "Spara protokollet på enheten eller dela det vidare."}
                            </DialogDescription>
                            <div className="native-v3-export-dialog-actions">
                                {!exportMode ? (
                                    <>
                                        <button type="button" onClick={() => setExportMode("share")}>
                                            <Share2 aria-hidden="true" />
                                            <span><strong>Dela</strong><small>Välj app eller mottagare</small></span>
                                        </button>
                                        <button type="button" onClick={() => setExportMode("save")}>
                                            <FolderDown aria-hidden="true" />
                                            <span><strong>Spara</strong><small>Ladda ned till Hämtade filer</small></span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" onClick={() => handleExport("pdf", exportMode)}>
                                            <FileText aria-hidden="true" />
                                            <span><strong>PDF</strong><small>Läsbart matchprotokoll</small></span>
                                        </button>
                                        <button type="button" onClick={() => handleExport("csv", exportMode)}>
                                            <Sheet aria-hidden="true" />
                                            <span><strong>CSV</strong><small>Tabellformat för vidare bearbetning</small></span>
                                        </button>
                                        <button
                                            type="button"
                                            className="native-v3-export-dialog-back"
                                            onClick={() => setExportMode(null)}
                                        >
                                            <ArrowLeft aria-hidden="true" />
                                            <span><strong>Tillbaka</strong></span>
                                        </button>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Card className="native-v3-saved-riders overflow-hidden">
                        <CardHeader className="native-v3-saved-riders-header space-y-2">
                            <CardTitle>Förarstatistik</CardTitle>
                            <CardDescription>Summering utifrån ditt protokoll</CardDescription>
                        </CardHeader>
                        <CardContent className="native-v3-saved-riders-content space-y-4">
                            {(["home", "away"]).map((teamKey) => {
                                const label = teamKey === "home"
                                    ? data?.match_details?.home_team ?? "Hemmalag"
                                    : data?.match_details?.away_team ?? "Bortalag";
                                const riders = riderTotals[teamKey] || [];
                                return (
                                    <section key={`team-${teamKey}`} className="native-v3-saved-rider-team space-y-2.5">
                                        <div className="mb-2.5 flex items-center justify-between gap-3">
                                            <h3 className="text-sm font-semibold text-foreground">
                                                {label}
                                            </h3>
                                            <span className="text-[11px] text-muted-foreground">
                                                {riders.length} förare
                                            </span>
                                        </div>

                                        {riders.length === 0 ? (
                                            <div className="native-v3-saved-rider-empty border border-dashed border-border/60 bg-background/70 px-3 py-4 text-center text-xs text-muted-foreground">
                                                Inga registrerade poäng.
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {riders.map((rider) => (
                                                    <div
                                                        key={rider.riderId}
                                                        className="native-v3-saved-rider-row flex items-center justify-between gap-3 border border-border/60 bg-background px-3 py-2.5"
                                                    >
                                                        <div className="min-w-0">
                                                            <div className="min-w-0">
                                                                <p className="truncate font-medium text-foreground">
                                                                    <span className="inline-flex items-center gap-2">
                                                                        <span>{rider.name}</span>
                                                                        {isGuestRiderForTeam(riderContextMap.get(String(rider.riderId)), riderContextMap.get(String(rider.riderId))?.context_team_id) ? (
                                                                            <GuestRiderBadge className="shrink-0" />
                                                                        ) : null}
                                                                    </span>
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {rider.heats} heat
                                                                    {rider.bonus ? ` • ${rider.bonus} bonus` : ""}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="shrink-0 text-right">
                                                            <div className="text-sm font-semibold text-foreground">
                                                                {rider.points}{rider.bonus ? `+${rider.bonus}` : ""}
                                                            </div>
                                                            <p className="text-[11px] text-muted-foreground">
                                                                poäng
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                        </CardContent>
                    </Card>
                </div>
            )}
            </div>
        </div>
    );
}
